import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import { CircularProgress } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import AcademicTableData from "Views/UsefulCorners/Academic/data/AcademicTableData";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import { Link } from "react-router-dom";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import api from "utils/api";
import { toast } from "react-toastify";

function Tables() {
  const [academics, setAcademics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedAcademic, setSelectedAcademic] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch all academics
  const fetchAcademics = async () => {
    setLoading(true);
    try {
      const response = await api.get("/academics");
      if (response.data.success) {
        setAcademics(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching academics:", error);
      toast.error(error.response?.data?.message || "Failed to fetch academics");
      setAcademics([]);
    } finally {
      setLoading(false);
    }
  };

  // Delete academic
  const handleDelete = async () => {
    if (!selectedAcademic?._id) return;

    setLoading(true);
    try {
      const response = await api.delete(`/academics/${selectedAcademic._id}`);
      if (response.data.success) {
        toast.success(response.data.message || "Academic deleted successfully!");
        setOpen(false);
        setSelectedAcademic(null);
        fetchAcademics();
      }
    } catch (error) {
      console.error("Error deleting academic:", error);
      toast.error(error.response?.data?.message || "Failed to delete academic");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (academic) => {
    setSelectedAcademic(academic);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedAcademic(null);
  };

  // Search filter
  const filteredAcademics = academics.filter((academic) => {
    const search = searchTerm.toLowerCase();
    return (
      academic.name?.toLowerCase().includes(search) ||
      academic.subCategoryTitle?.toLowerCase().includes(search) ||
      academic.vacancy?.toLowerCase().includes(search)
    );
  });

  const { columns, rows } = AcademicTableData(filteredAcademics, handleDeleteClick, fetchAcademics);

  // Fetch academics on component mount
  useEffect(() => {
    fetchAcademics();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={2}>
          <Card>
            <ArgonBox p={2}>
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" p={3}>
                <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                  All Academic ({filteredAcademics.length})
                </ArgonTypography>
                <ArgonBox display="flex" alignItems="center">
                  <ArgonInput
                    placeholder="Search by name, title, or vacancy"
                    name="search"
                    size="small"
                    type="text"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ margin: "10px", minWidth: "250px" }}
                  />
                  <ArgonButton
                    size="small"
                    component={Link}
                    to="/academic/addacademic"
                    variant="contained"
                    color="primary"
                  >
                    Add
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            {loading ? (
              <ArgonBox display="flex" justifyContent="center" alignItems="center" py={5}>
                <CircularProgress />
              </ArgonBox>
            ) : filteredAcademics.length === 0 ? (
              <ArgonBox display="flex" justifyContent="center" alignItems="center" py={5}>
                <ArgonTypography variant="body2" color="text">
                  {searchTerm
                    ? "No academics found matching your search"
                    : "No academics available. Add one to get started!"}
                </ArgonTypography>
              </ArgonBox>
            ) : (
              <ArgonBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                <Table columns={columns} rows={rows} />
              </ArgonBox>
            )}
          </Card>
        </ArgonBox>
      </ArgonBox>

      <DeleteDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleDelete}
        title="Are you sure you want to delete this academic?"
        description="This action is irreversible. Please be certain."
        cancelText="Cancel"
        confirmText="Delete"
        loading={loading}
      />
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
